/* =====================================================
   Operator Selector Component – Version 2.1 (Modified: July 31, 2025)
   Interactive buttons for selecting mathematical operators (reusable)
   Date Modified: 31 July 2025 | Time: Current
===================================================== */

import { memo, useCallback } from 'react'
import { useGameState } from '../hooks/useGameState'
import styles from './OperatorSelector.module.css'

// Available mathematical operators
const OPERATORS = [
  { value: '+', display: '+', label: 'Addition' },
  { value: '-', display: '−', label: 'Subtraction' },
  { value: '*', display: '×', label: 'Multiplication' },
  { value: '/', display: '÷', label: 'Division' },
]

/**
 * Operator selection component
 * Operators can be reused multiple times per equation
 */
function OperatorSelector() {
  const { placeValue } = useGameState()

  /**
   * Handle operator button click
   */
  const handleOperatorClick = useCallback((operatorValue) => {
    placeValue(operatorValue, 'operator')
  }, [placeValue])

  return (
    <div className={styles.selectGroup}>
      <h4>Reusable</h4>
      <div className={styles.selectArea}>
        {OPERATORS.map((operator) => (
          <OperatorButton
            key={operator.value}
            operator={operator}
            onClick={handleOperatorClick}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Individual operator button component
 * Memoized to prevent unnecessary re-renders
 */
const OperatorButton = memo(function OperatorButton({ operator, onClick }) {
  const handleClick = useCallback(() => {
    onClick(operator.value)
  }, [operator.value, onClick])

  return (
    <button
      className={`${styles.select} ${styles.operatorButton}`}
      onClick={handleClick}
      data-value={operator.value}
      data-type="operator"
      title={operator.label}
    >
      {operator.display}
    </button>
  )
})

export default OperatorSelector